import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Button } from "@material-ui/core";
import MessageDialog, { useMessageDialog } from "../Common/MessageDialog";
import { useCSDialog } from "../Common/CSDialog";
import { submitGroupTermination } from "../../api/groupApi";

// ─── Component ────────────────────────────────────────────────────────────────

const GroupTermination = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const groupNumber = searchParams.get("groupNumber") ?? "";
    const planYear = searchParams.get("planYear") ?? new Date().getFullYear().toString();

    const [isProcessing, setIsProcessing] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const { showMessage: showError, dialogProps: errorDialogProps } = useMessageDialog();
    const [showConfirm, confirmDialog] = useCSDialog();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            groupNumber,
            planYear,
            requestedTerminationDate: "",
        },
    });

    // ─── Submit ────────────────────────────────────────────────────────────────

    const onSubmit = async (data) => {
        const confirmed = await showConfirm({
            mode: "confirmation",
            title: "Confirm Termination",
            message: "Please confirm you are submitting a group termination request.",
            confirmText: "Confirm",
        });
        if (!confirmed) return;

        setIsProcessing(true);
        try {
            // const payload = {
            //     groupNumber,
            //     planYear,
            //     requestedTerminationDate: data.requestedTerminationDate,
            // };
            const payload = {
                groupNumber: groupNumber || null,
                planYear: planYear ? parseInt(planYear, 10) : null,
                requestedTerminationDate: data.requestedTerminationDate || null,
            };
            // TODO: replace with real API call            
            await submitGroupTermination(payload);
            console.log("GROUP TERMINATION SUBMITTED:", JSON.stringify(payload, null, 2));
            setIsConfirmed(true);
        } catch (err) {
            await showError({
                message: `An error occurred while submitting your request: ${err.message}`,
                title: "YourMedicareSolutions.com",
                messageType: "danger",
            });
        }
        setIsProcessing(false);
    };

    // ─── Confirmation screen ───────────────────────────────────────────────────

    if (isConfirmed) {
        return (
            <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content">
                <div className="row d-md-flex">
                    <section className="col-sm-12 internal-page">
                        <div className="region region-content">

                            <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
                                <div className="breadcrumb-wrap">
                                    <div className="region--breadcrumb nst-1">
                                        <section className="block-system block-system-breadcrumb-block">
                                            <div role="navigation">
                                                <ol className="breadcrumb">
                                                    <li><Link to="/">Home</Link></li>
                                                    <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                                                    <li>Group Termination</li>
                                                </ol>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </section>

                            <h1 className="page-header">Group Termination</h1>
                            <div style={{ padding: "24px", border: "1px solid #d4edda", borderRadius: "6px", background: "#f0f9f0", textAlign: "center" }}>
                                <div style={{ fontSize: "56px", color: "#16a34a", lineHeight: 1 }}>✓</div>
                                <h2 style={{ color: "#0056a3" }}>Group Termination Submission Received</h2>
                                <p>Thank you. Your group termination request has been submitted successfully.</p>
                                <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>
                                    {/* <Button
                                        className="webform-button--next button js-form-submit form-submit btn-default btn"
                                        onClick={() => navigate(`/GroupDashboard?groupNumber=${groupNumber}`)}
                                    >
                                        Back to Group Dashboard
                                    </Button> */}
                                    <Button
                                        className="webform-button--next button js-form-submit form-submit btn-default btn"
                                        onClick={() => navigate(`/GroupLogin`)}
                                    >
                                        Group Plan Management
                                    </Button>
                                </div>
                            </div>

                        </div>
                    </section>
                </div>
            </div>
        );
    }

    // ─── Form ──────────────────────────────────────────────────────────────────

    return (
        <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content">
            <div className="row d-md-flex">
                <section className="col-sm-12 internal-page">
                    <div className="region region-content">

                        {/* Breadcrumb */}
                        <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
                            <div className="breadcrumb-wrap">
                                <div className="region--breadcrumb nst-1">
                                    <section className="block-system block-system-breadcrumb-block">
                                        <div role="navigation">
                                            <ol className="breadcrumb">
                                                <li><Link to="/">Home</Link></li>
                                                <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                                                <li>Group Termination</li>
                                            </ol>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </section>

                        <h1 className="page-header">Group Termination</h1>
                        <p>Complete this form to request termination of your Employer Group Part D plan.</p>

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>

                            {/* ══ GROUP INFORMATION ══════════════════════════════════════════ */}
                            <div style={{ padding: "20px", border: "1px solid #e0e0e0", borderRadius: "6px", backgroundColor: "#fafbfd", marginTop: "32px" }}>
                                <h2 style={{ color: "#0056a3", marginTop: 0, marginBottom: "16px" }}>Group Information</h2>

                                {/* Group Number · Current Plan Year */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="groupNumber" className="control-label">Group Number</label>
                                        <input
                                            id="groupNumber"
                                            type="text"
                                            className="form-text form-control"
                                            readOnly
                                            {...register("groupNumber")}
                                        />
                                    </div>
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="planYear" className="control-label">Current Plan Year</label>
                                        <input
                                            id="planYear"
                                            type="text"
                                            className="form-text form-control"
                                            readOnly
                                            {...register("planYear")}
                                        />
                                    </div>
                                </div>

                                {/* Requested Termination Date */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="requestedTerminationDate" className="control-label js-form-required form-required">
                                            Requested Termination Date
                                        </label>
                                        <input
                                            id="requestedTerminationDate"
                                            type="date"
                                            className="form-text required form-control"
                                            {...register("requestedTerminationDate", { required: "Requested Termination Date is required." })}
                                        />
                                        <ErrorMessage errors={errors} name="requestedTerminationDate"
                                            render={({ message }) => <div role="alert" aria-live="polite" className="error">{message}</div>} />
                                    </div>
                                </div>
                            </div>

                            {/* ── Dialogs ───────────────────────────────────────────────────── */}
                            {confirmDialog}
                            <MessageDialog {...errorDialogProps} />

                            {/* ── Submit ───────────────────────────────────────────────────── */}
                            <Button
                                type="submit"
                                className="webform-button--next button js-form-submit form-submit btn-default btn"
                                disabled={isProcessing}
                                style={{ marginTop: "24px" }}
                            >
                                {isProcessing ? "Submitting…" : "Submit Termination"}
                            </Button>

                        </form>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default GroupTermination;
